import Timeline, {
  DateHeader,
  TimelineHeaders,
  TodayMarker,
} from "react-calendar-timeline";
import { DateTime } from "luxon";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrder } from "redux/actions/order";
import {
  Box,
  CircularProgress,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  InputLabel,
  ListItemText,
  MenuItem,
  TextField,
  Theme,
  Typography,
} from "@material-ui/core";
import { FORMAT_TYPE } from "utils/DateTimeUtils";
import {
  addCastShift,
  fetchCastShift,
  updateCastShift,
} from "redux/actions/castShift";
import EnumUtils from "../../utils/EnumUtils";
import OrderStatus from "../../types/enum/OrderStatus";
import { fetchShops } from "redux/actions/shop";
import { fetchRoom } from "redux/actions/room";
import CastScheduleShiftItem from "./components/CastScheduleShiftItem";
import CastScheduleOrderItem from "./components/CastScheduleOrderItem";
import useModal from "../../hooks/useModal";
import CastScheduleOnItemMove from "./components/CastScheduleOnItemMove";
import OrderRes from "../../types/res/order/OrderRes";
import CastScheduleOnItemResize from "./components/CastScheduleOnItemResize";
import CreateOrderReq from "../../types/req/order/CreateOrderReq";
import CastScheduleOnItemCreate from "./components/CastScheduleOnItemCreate";
import { fetchAdditionalTime } from "redux/actions/additionalTime";
import CastScheduleOnItemMoveChangeClass from "./components/castScheduleOnItemMoveChangeClass";
import ShiftStatus from "../../types/enum/ShiftStatus";
import CastShiftRes from "../../types/res/castShift/CastShiftRes";
import CastScheduleOnShiftMove from "./components/CastScheduleOnShiftMove";
import FormModal from "../../components/FormModal";
import CreateCastShiftReq from "../../types/req/castShift/CreateCastShiftReq";
import UpdateCastShiftReq from "../../types/req/castShift/UpdateCastShiftReq";
import { FormType } from "components/FormField";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import Select from "@material-ui/core/Select";
import Input from "@material-ui/core/Input";
import Checkbox from "@material-ui/core/Checkbox";
import TimeUtils from "utils/TimeUtils";
import CastScheduleOnItemCreateNoCast from "pages/CastSchedule/components/CastScheduleOnItemCreateNoCast";
import styled from "styled-components";
import ShopRes from "types/res/shop/ShopRes";
import { fetchNotelClass } from "redux/actions/notelClass";
import { CastScheduleItemColor } from "types/enum/CastScheduleItemColor";
import CastRes from "types/res/cast/CastRes";
import { fetchCastCategory } from "redux/actions/castCategory";
import CastApi from "api/CastApi";
import CastShiftApi from "api/CastShiftApi";
import ReactGA from "react-ga4";
import DayNav from "components/DayNav";
import Tooltip from "rc-tooltip";

const CastSchedule: React.FC = () => {
  const keys = {
    groupIdKey: "id",
    groupTitleKey: "title",
    groupRightTitleKey: "rightTitle",
    itemIdKey: "id",
    itemTitleKey: "title",
    itemDivTitleKey: "title",
    itemGroupKey: "group",
    itemTimeStartKey: "start_time",
    itemTimeEndKey: "end_time",
  };
  const Item = styled.div`
    border: 1px solid ${(props) => props.color} !important;
    border-radius: 4px;
    height: 60px !important;
    background-color: gray !important;
  `;
  const dispatch = useDispatch();
  const [castScheduleOnItemMove, setShowMoveForm] = useModal(
    "castScheduleOnItemMove"
  );
  const [castScheduleOnItemMoveChangeClass, setShowMoveChangeClassForm] =
    useModal("castScheduleOnItemMoveChangeClass");
  const [castScheduleOnItemResize, setShowResizeForm] = useModal(
    "castScheduleOnItemResize"
  );
  const [castScheduleOnItemCreate, setShowCreateForm] = useModal(
    "castScheduleOnItemCreate"
  );
  const [castScheduleOnItemCreateNoCast, setShowCreateNoCastForm] = useModal(
    "castScheduleOnItemCreateNoCast"
  );
  const [castScheduleOnShiftMove, setShowShiftMoveForm] = useModal(
    "castScheduleOnShiftMove"
  );
  const [castSchedule, setCastForm] = useModal("castSchedule");
  const companyId = useSelector((state) => state.account.staff.companyId);
  const orders = useSelector((state) => state.order);
  const castShifts = useSelector((state) => state.castShift);
  const shops = useSelector((state) => state.shop);
  const castCategories = useSelector((state) => state.castCategory);
  const notelClasses = useSelector((state) => state.notelClass);
  // visit (来店型) 業態判定。visit 時は groups を「未アサイン + 部屋」 に切替。
  // super admin の企業切替を考慮し、state.company から current company を引く。
  const companies = useSelector((state) => state.company);
  const loginBusinessType = useSelector(
    (state) => state.account.staff.company.companyGroup.businessType
  );
  const currentCompany = companies?.find((c) => c.companyId === companyId);
  const isVisit =
    (currentCompany?.companyGroup?.businessType ?? loginBusinessType) ===
    "visit";
  const rooms = useSelector((state) => state.room);
  const [stateOrders, setStateOrders] = useState<OrderRes[]>([]);
  const [stateShifts, setStateShifts] = useState<CastShiftRes[]>([]);
  // visit (来店型) sticky overlay 用: 未アサインのアイテム (lane 付き)
  const [unassignedItems, setUnassignedItems] = useState<
    Array<{
      id: string;
      shiftId: number;
      castDisplayName: string;
      title: string;
      start_time: number;
      end_time: number;
      planWorkStart: Date;
      planWorkEnd: Date;
      color: string;
      lane: number;
      castShift: any;
    }>
  >([]);
  // ドロップターゲット計算用の Timeline DOM ref
  const timelineWrapperRef = useRef<HTMLDivElement | null>(null);
  // canvas (時刻軸エリア) の幅 — bars の left/width 計算に使用
  const [canvasWidth, setCanvasWidth] = useState(0);
  // 現在 drag 中の shift (視覚フィードバック用)
  const [draggingShiftId, setDraggingShiftId] = useState<number | null>(null);
  // visit (来店型) DnD 確定ポップアップ用の保留状態。確定するまで Timeline 上のバーは元位置のまま。
  const [pendingRoomMove, setPendingRoomMove] = useState<{
    shiftId: number;
    castDisplayName: string;
    fromLabel: string;
    toLabel: string;
    newRoomId: number | null;
    originalRoomId: number | null;
    originalPlanWorkStart: Date;
    originalPlanWorkEnd: Date;
    newPlanWorkStart: Date;
    newPlanWorkEnd: Date;
  } | null>(null);
  const [selectShops, setSelectShops] = useState<string[]>([]);
  const [selectNotelClasses, setSelectNotelClasses] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const changeDateTime = useSelector(
    (state) => state.account.staff.company.changeDateTime
  );
  const changeDate = DateTime.fromFormat(changeDateTime, "HH:mm:ss");
  const [startTime, setStartTime] = useState<number>();
  const [endTime, setEndTime] = useState<number>();
  const [startDate, setStartDate] = useState(
    DateTime.local()
      .minus({ hours: changeDate.hour, minutes: changeDate.minute })
      .toJSDate()
  );
  const [endDate, setEndDate] = useState(
    DateTime.local()
      .minus({ hours: changeDate.hour, minutes: changeDate.minute })
      .toJSDate()
  );
  const [minDate, setMinDate] = useState(
    DateTime.local()
      .minus({ hours: changeDate.hour, minutes: changeDate.minute })
      .toJSDate()
  );
  const [maxDate, setMaxDate] = useState(
    DateTime.local()
      .minus({ hours: changeDate.hour, minutes: changeDate.minute })
      .toJSDate()
  );
  const [groups, setGroups] = useState<
    (
      | {
          id: string;
          title: any;
          root: boolean;
          stackItems: boolean;
          shopId: number | null;
          height: number;
        }
      | any
    )[]
  >([]);
  const [items, setItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sort, setSort] = useState("time");
  const [filterCastName, setFilterCastName] = useState("");
  const [casts, setCasts] = useState<CastRes[]>([]);
  const forms = [
    {
      label: "キャスト名",
      key: "castId",
      type: FormType.AutoCompleteSelect,
      options: casts.map((c) => ({ id: c.castId, name: c.displayName })),
      loadMoreResults: async (displayName?: string) => {
        const offset = Math.trunc(casts.length / 100);
        const castCategoryId = castCategories.find((castCategory) =>
          castCategory.name.includes("在籍")
        )?.castCategoryId;
        const c = await CastApi.findAllWithPagination(companyId, {
          limit: 100,
          offset: displayName ? 0 : offset,
          displayName,
          castCategoryIds: castCategoryId ? [castCategoryId] : undefined,
        });
        setCasts((prev) =>
          Array.from(
            new Map([...prev, ...c].map((cast) => [cast.castId, cast])).values()
          )
        );

        return c;
      },
    },
    {
      label: "店舗名",
      key: "shops",
      type: FormType.MultiOptions,
      options: shops.map((s) => ({ id: s.shopId, name: s.name })),
      value: (val: any) => val.map((s: any) => s.shopId),
    },
    {
      label: "予定勤務開始",
      key: "planWorkStart",
      type: FormType.IntervalDateTime,
      minuteStep: 15,
    },
    {
      label: "予定勤務終了",
      key: "planWorkEnd",
      type: FormType.IntervalDateTime,
      minuteStep: 15,
    },
    {
      label: "実勤務開始",
      key: "actualWorkStart",
      type: FormType.IntervalDateTime,
      minuteStep: 5,
    },
    {
      label: "実勤務終了",
      key: "actualWorkEnd",
      type: FormType.IntervalDateTime,
      minuteStep: 5,
    },
    {
      label: "休憩開始",
      key: "workOffStart",
      type: FormType.IntervalDateTime,
      minuteStep: 5,
    },
    {
      label: "休憩終了",
      key: "workOffEnd",
      type: FormType.IntervalDateTime,
      minuteStep: 5,
    },
    {
      label: "ステータス",
      key: "status",
      type: FormType.Select,
      options: Object.entries(ShiftStatus).map(([key, value]) => ({
        id: key,
        name: value,
      })),
    },
    {
      label: "スタッフメモ",
      key: "staffMemo",
      type: FormType.Text,
    },
    {
      label: "キャストメモ",
      key: "castMemo",
      type: FormType.Text,
    },
    {
      label: "スケジュールカラー",
      key: "scheduleItemColor",
      type: FormType.Select,
      options: Object.entries(CastScheduleItemColor).map(([key, value]) => ({
        id: key,
        name: value,
      })),
    },
    {
      label: "営業時間無視",
      key: "isForceShift",
      type: FormType.Boolean,
      options: [
        { id: "0", name: "オフ" },
        { id: "1", name: "オン" },
      ],
    },
  ];

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      button: {
        margin: theme.spacing(1),
      },
    })
  );
  const classes = useStyles();

  const defaultSortFunc = (
    a: any,
    b: any,
    key: string,
    direction = 1,
    nullsFirst = 1
  ) => {
    if (a[key] == undefined && b[key] == undefined) return 0;
    if (a[key] == undefined) return nullsFirst * 1;
    if (b[key] == undefined) return nullsFirst * -1;
    if (a[key] > b[key]) return direction * 1;
    if (a[key] < b[key]) return direction * -1;
    return 0;
  };
  const sortFunc = (data: any[], keys: string[]) => {
    const _data = data.slice();
    _data.sort((a, b) => {
      let order = 0;
      keys.some((key) => {
        order = defaultSortFunc(a, b, key);
        return !!order;
      });
      return order;
    });
    return _data;
  };

  useEffect(() => {
    setSelectShops([...shops.map((shop) => String(shop.shopId)), "すべて"]);
  }, [shops]);

  useEffect(() => {
    setSelectNotelClasses([
      ...notelClasses.map((notelClass) => String(notelClass.notelClassId)),
      "すべて",
    ]);
  }, [notelClasses]);

  // visit (来店型) sticky overlay 用に Timeline canvas width を計測
  useEffect(() => {
    if (!isVisit) return;
    const SIDEBAR = 300;
    const updateW = () => {
      if (timelineWrapperRef.current) {
        const rect = timelineWrapperRef.current.getBoundingClientRect();
        setCanvasWidth(Math.max(0, rect.width - SIDEBAR));
      }
    };
    updateW();
    window.addEventListener("resize", updateW);
    return () => window.removeEventListener("resize", updateW);
  }, [isVisit, isLoading]);

  useEffect(() => {
    window.addEventListener(
      "wheel",
      (e) => {
        if (e.deltaX) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      },
      { passive: false }
    );
  }, []);

  useEffect(() => {
    dispatch(fetchShops(companyId));
    dispatch(fetchAdditionalTime(companyId));
    dispatch(fetchNotelClass(companyId));
    if (isVisit) {
      dispatch(fetchRoom(companyId));
    }
    const apiCall = async () => {
      const result = await fetchCastCategory(companyId)(dispatch);
      if (result) {
        const castCategoryId = result.find((r) =>
          r.name.includes("在籍")
        )?.castCategoryId;
        const castRes = await CastApi.findAllWithPagination(companyId, {
          limit: 100,
          offset: 0,
          castCategoryIds: castCategoryId ? [castCategoryId] : undefined,
        });
        setCasts(castRes);
      }
    };
    apiCall();
  }, [dispatch, companyId]);

  useEffect(() => {
    const apiCall = async () => {
      setIsLoading(true);
      await dispatch(
        fetchCastShift(
          companyId,
          DateTime.fromJSDate(startDate).toFormat(FORMAT_TYPE.YEAR_DAY),
          DateTime.fromJSDate(endDate).toFormat(FORMAT_TYPE.YEAR_DAY)
        )
      );
      await dispatch(
        fetchOrder(
          companyId,
          DateTime.fromJSDate(startDate).toFormat(FORMAT_TYPE.YEAR_DAY),
          DateTime.fromJSDate(endDate)
            .plus({ days: TimeUtils.isInAllDays(shops) ? 1 : 0 })
            .toFormat(FORMAT_TYPE.YEAR_DAY)
        )
      );
      setIsLoading(false);
    };
    apiCall();
  }, []);

  useEffect(() => {
    setStateOrders([...orders]);
  }, [orders]);

  useEffect(() => {
    setStateShifts(castShifts);
  }, [castShifts]);

  useEffect(() => {
    // visit (来店型) 業態: groups を「未アサイン + 店舗仕切り + 部屋」 に置換。
    // delivery 既存パターン (root: true で店舗仕切り、その下にキャスト行) を踏襲し、
    // 店舗ごとに root 行 → 配下に部屋行を並べる。
    if (isVisit) {
      // 全店舗を仕切り行として出す (rooms 未登録の店舗も「(部屋未登録)」 行を 1 つ置く)。
      // selectShops が指定されてればフィルタ、未指定なら全店舗。
      const filteredShops = (shops || []).filter((s: any) =>
        selectShops && selectShops.length > 0
          ? selectShops.map((x) => Number(x)).includes(s.shopId)
          : true
      );
      const sortedShops = [...filteredShops].sort(
        (a: any, b: any) => a.shopId - b.shopId
      );
      // D-1: 未アサインは Timeline 外の sticky overlay で描画するため groups には含めない。
      const visitGroups: any[] = [];
      for (const shop of sortedShops) {
        // 店舗仕切り行 (root: true)
        visitGroups.push({
          id: `shop-${shop.shopId}`,
          title: (
            <div style={{ whiteSpace: "nowrap", overflow: "visible" }}>
              {shop.name}
            </div>
          ),
          root: true,
          stackItems: false,
          shopId: shop.shopId,
          notelClassId: null,
          status: 1,
          height: 30,
        });
        // 該当店舗の部屋 (isActive のみ、sort 順)
        const shopRooms = (rooms || [])
          .filter((r: any) => r.isActive && r.shopId === shop.shopId)
          .sort(
            (a: any, b: any) => (a.sort ?? 9999) - (b.sort ?? 9999)
          );
        if (shopRooms.length === 0) {
          // 部屋未登録の店舗は仕切りだけ表示 (プレースホルダ行は出さない)
          continue;
        }
        for (const room of shopRooms) {
          const hoverText = room.note
            ? `${room.name}\n${room.note}`
            : room.name;
          visitGroups.push({
            id: `room-${room.roomId}`,
            title: (
              <div
                title={hoverText}
                style={{
                  paddingLeft: "20px",
                  paddingRight: "8px",
                  lineHeight: 1.3,
                }}
              >
                <div style={{ whiteSpace: "nowrap" }}>{room.name}</div>
                {room.note && (
                  <div
                    style={{
                      fontSize: 13,
                      color: "#666",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      marginTop: 2,
                    }}
                  >
                    {room.note}
                  </div>
                )}
              </div>
            ),
            root: false,
            stackItems: true,
            shopId: room.shopId,
            notelClassId: null,
            status: 1,
            height: room.note ? 110 : 70,
          });
        }
      }
      // 未アサインは別 Timeline で描画するため bottom には含めない。
      setGroups(visitGroups);
      return;
    }
    let newGroups: any[] = [];
    if (sort !== "time") {
      newGroups = sortFunc(
        stateShifts
          .flatMap((castShift) => {
            return castShift.cast?.castNames
              ?.filter((group) => {
                const shopCondition = castShift.shops
                  .map((shop) => shop.shopId)
                  .includes(group.shopId);
                const classCondition = selectNotelClasses.includes("すべて")
                  ? true
                  : selectNotelClasses
                      .map((notelClass) => Number(notelClass))
                      .includes(group.notelClassId);
                const castNameCondition = !filterCastName
                  ? true
                  : group.name.includes(filterCastName);
                return shopCondition && classCondition && castNameCondition;
              })
              .flatMap((castName) => [
                {
                  id: `shop-${castName.shopId}`,
                  title: <div>{castName?.shop?.name}</div>,
                  root: true,
                  stackItems: false,
                  shopId: castName.shopId,
                  notelClassId: castName.notelClassId,
                  status:
                    EnumUtils.mapToEnum(ShiftStatus, castShift.status) ===
                    ShiftStatus.absence
                      ? 2
                      : 1,
                  height: 30,
                },
                {
                  id: `castName-${castName.castNameId}`,
                  title: (
                    <div style={{ paddingLeft: "20px" }}>
                      {castName.name}({castName?.notelClass?.name})
                    </div>
                  ),
                  root: false,
                  stackItems: true,
                  shopId: castName.shopId,
                  notelClassId: castName.notelClassId,
                  status:
                    EnumUtils.mapToEnum(ShiftStatus, castShift.status) ===
                    ShiftStatus.absence
                      ? 2
                      : 1,
                  height: 100,
                },
              ]);
          })
          .filter(Boolean),
        ["shopId", "status", "notelClassId"]
      );
    } else {
      newGroups = sortFunc(
        stateShifts
          .flatMap((castShift) => {
            return castShift.cast?.castNames
              ?.filter((group) => {
                const shopCondition = castShift.shops
                  .map((shop) => shop.shopId)
                  .includes(group.shopId);
                const classCondition = selectNotelClasses.includes("すべて")
                  ? true
                  : selectNotelClasses
                      .map((notelClass) => Number(notelClass))
                      .includes(group.notelClassId);
                const castNameCondition = !filterCastName
                  ? true
                  : group.name.includes(filterCastName);

                return shopCondition && classCondition && castNameCondition;
              })
              .flatMap((castName) => [
                {
                  id: `shop-${castName.shopId}`,
                  title: <div>{castName?.shop?.name}</div>,
                  root: true,
                  stackItems: false,
                  shopId: castName.shopId,
                  notelClassId: castName.notelClassId,
                  status:
                    EnumUtils.mapToEnum(ShiftStatus, castShift.status) ===
                    ShiftStatus.absence
                      ? -1
                      : 1,

                  height: 30,
                  startTime: 0,
                },
                {
                  id: `castName-${castName.castNameId}`,
                  title: (
                    <div style={{ paddingLeft: "20px" }}>
                      {castName.name}({castName?.notelClass?.name})
                    </div>
                  ),
                  root: false,
                  stackItems: true,
                  shopId: castName.shopId,
                  notelClassId: castName.notelClassId,
                  status:
                    EnumUtils.mapToEnum(ShiftStatus, castShift.status) ===
                    ShiftStatus.absence
                      ? 2
                      : 1,
                  height: 100,
                  startTime:
                    (
                      castShift.actualWorkEnd || castShift.planWorkEnd
                    ).getTime() < new Date().getTime()
                      ? DateTime.fromJSDate(castShift.planWorkStart)
                          .plus({ days: 2 })
                          .toJSDate()
                          .getTime()
                      : (
                          castShift.actualWorkStart || castShift.planWorkStart
                        ).getTime(),
                },
              ]);
          })
          .filter(Boolean),
        ["shopId", "status", "startTime"]
      );
    }
    const filterGroups =
      newGroups.length > 0
        ? Array.from(
            newGroups
              .reduce(
                (map, currentItem) =>
                  currentItem.root
                    ? map.set(currentItem.id, currentItem)
                    : map.set(
                        `${currentItem.id}-${currentItem.shopId}`,
                        currentItem
                      ),
                new Map()
              )
              .values()
          ).filter((group: any) =>
            selectShops
              ? selectShops.map((shop) => Number(shop)).includes(group.shopId)
              : true
          )
        : [];
    const unAssign = {
      id: `0`,
      title: <div>未アサイン</div>,
      root: false,
      stackItems: true,
      shopId: null,
      height: 30,
    };
    filterGroups.unshift(unAssign);
    setGroups(filterGroups);
  }, [
    stateShifts,
    selectShops,
    selectNotelClasses,
    sort,
    filterCastName,
    isVisit,
    rooms,
  ]);

  useEffect(() => {
    if (!shops.length) return;
    setStartTime(
      Number(
        shops.reduce(
          (a, b) =>
            a < Number(b.openingTime.split(":")[0])
              ? a
              : Number(b.openingTime.split(":")[0]),
          24
        )
      )
    );
    setEndTime(TimeUtils.maxClosingTimeInShop(shops));
  }, [shops]);

  useEffect(() => {
    const startAndMinDate = DateTime.local()
      .minus({ hours: changeDate.hour, minutes: changeDate.minute })
      .startOf("day")
      .plus({ hours: startTime })
      .toJSDate();
    const endAndMaxDate = DateTime.local()
      .minus({ hours: changeDate.hour, minutes: changeDate.minute })
      .startOf("day")
      .plus({ hours: endTime })
      .toJSDate();
    setStartDate(startAndMinDate);
    setEndDate(endAndMaxDate);
  }, [startTime, endTime]);

  useEffect(() => {
    setLoading(true);
    // visit (来店型): castShifts を room_id で振り分けて items 生成。
    // delivery ロジックには触れず early return で完結。
    if (isVisit) {
      const fmt = (d: Date) =>
        DateTime.fromJSDate(d).toFormat("HH:mm");
      const roomShiftItems: any[] = [];
      const unassignedSource: any[] = [];
      for (const shift of stateShifts as any[]) {
        const castName = shift.cast?.castNames?.[0];
        if (!castName) continue;
        const startStr = fmt(shift.planWorkStart);
        const endStr = fmt(shift.planWorkEnd);
        const statusLabel =
          EnumUtils.mapToEnum(ShiftStatus, shift.status) ===
          ShiftStatus.absence
            ? "当日欠勤"
            : shift.actualWorkStart
            ? "実出勤"
            : "出勤予定";
        const title = `${castName.name} ${startStr}〜${endStr} (${statusLabel})`;
        const color = shift.scheduleItemColor
          ? shift.scheduleItemColor
          : shiftColor(shift.status);
        if (shift.roomId) {
          roomShiftItems.push({
            id: `shift-${shift.castShiftId}`,
            group: `room-${shift.roomId}`,
            title,
            color,
            start_time: DateTime.fromJSDate(shift.planWorkStart).toMillis(),
            end_time: DateTime.fromJSDate(shift.planWorkEnd).toMillis(),
            castShift: shift,
            canChangeGroup: true,
            canMove: true,
            canResize: false,
          });
        } else {
          unassignedSource.push({
            shiftId: shift.castShiftId,
            castDisplayName: castName.name,
            title,
            start_time: DateTime.fromJSDate(shift.planWorkStart).toMillis(),
            end_time: DateTime.fromJSDate(shift.planWorkEnd).toMillis(),
            planWorkStart: shift.planWorkStart,
            planWorkEnd: shift.planWorkEnd,
            color,
            castShift: shift,
          });
        }
      }
      // lane 割当 (重なるアイテムは別 lane に配置、 sticky overlay の縦積み用)
      unassignedSource.sort((a, b) => a.start_time - b.start_time);
      const laneEnds: number[] = []; // 各 lane の最終終了時刻
      const laned = unassignedSource.map((u) => {
        let lane = -1;
        for (let i = 0; i < laneEnds.length; i++) {
          if (laneEnds[i] <= u.start_time) {
            lane = i;
            break;
          }
        }
        if (lane === -1) {
          lane = laneEnds.length;
          laneEnds.push(u.end_time);
        } else {
          laneEnds[lane] = u.end_time;
        }
        return {
          id: `shift-${u.shiftId}`,
          shiftId: u.shiftId,
          castDisplayName: u.castDisplayName,
          title: u.title,
          start_time: u.start_time,
          end_time: u.end_time,
          planWorkStart: u.planWorkStart,
          planWorkEnd: u.planWorkEnd,
          color: u.color,
          lane,
          castShift: u.castShift,
        };
      });
      // visit (来店型) Order バー描画:
      // - order.roomId 確定 → そのルーム行
      // - order.roomId null → cast の当該 shift.roomId にフォールバック (= キャストの居場所に表示)
      // - 両方 null → 描画スキップ (室なし / cast 未指定の予約は受注一覧で見る)
      const orderBars: any[] = [];
      for (const order of stateOrders as any[]) {
        const orderStatus = EnumUtils.mapToEnum(OrderStatus, order.status);
        if (
          orderStatus !== OrderStatus.booking &&
          orderStatus !== OrderStatus.paid
        ) {
          continue;
        }
        const inTime = order.planInTime || order.departureTime;
        const outTime = order.planOutTime;
        if (!inTime || !outTime) continue;
        let effectiveRoomId: number | null = order.roomId ?? null;
        if (effectiveRoomId == null && order.castName?.castId) {
          // cast の shift.roomId にフォールバック (order の planInTime を含む shift を優先)
          const inMillis = DateTime.fromJSDate(new Date(inTime)).toMillis();
          const matched = stateShifts.find((s: any) => {
            if (s.cast?.castId !== order.castName.castId) return false;
            if (s.roomId == null) return false;
            const sStart = DateTime.fromJSDate(s.planWorkStart).toMillis();
            const sEnd = DateTime.fromJSDate(s.planWorkEnd).toMillis();
            return inMillis >= sStart && inMillis <= sEnd;
          });
          effectiveRoomId = matched?.roomId ?? null;
        }
        if (!effectiveRoomId) continue;
        const guestName = order.guest?.name || "顧客未設定";
        // 送信履歴アイコン: 顧客 / キャスト / スタッフ それぞれの送信状況
        const customerIcon = order.sentToCustomerAt
          ? order.sendMethodCustomer === "sms"
            ? "📱"
            : "📧"
          : "";
        const castIcon = order.sentToCastAt
          ? order.sendMethodCast === "line"
            ? "💬"
            : "📧"
          : "";
        const staffIcon = order.sentToStaffAt ? "📧" : "";
        const sendIcons = [customerIcon, castIcon, staffIcon]
          .filter((s) => s.length > 0)
          .join("");
        const titleSuffix = sendIcons ? ` ${sendIcons}` : "";
        // visit 業態の Order バー title: 顧客名のみ + 送信絵文字 (短コースでも視認できるよう簡略)
        orderBars.push({
          id: `order-${order.orderId}`,
          group: `room-${effectiveRoomId}`,
          title: `${guestName}${titleSuffix}`,
          color: "#1976d2",
          start_time: DateTime.fromJSDate(new Date(inTime)).toMillis(),
          end_time: DateTime.fromJSDate(new Date(outTime)).toMillis(),
          order,
          canChangeGroup: false,
          canMove: false,
          canResize: false,
        });
        // 清掃時間バー: planOutTime ~ cleaningEndTime の半透明グレー (中央寄せ)
        if (order.cleaningEndTime) {
          const cleanStart = DateTime.fromJSDate(new Date(outTime)).toMillis();
          const cleanEnd = DateTime.fromJSDate(
            new Date(order.cleaningEndTime)
          ).toMillis();
          if (cleanEnd > cleanStart) {
            orderBars.push({
              id: `cleaning-${order.orderId}`,
              group: `room-${effectiveRoomId}`,
              title: `🧹 清掃中`,
              color: "rgba(120, 120, 120, 0.45)",
              start_time: cleanStart,
              end_time: cleanEnd,
              order,
              isCleaning: true,
              canChangeGroup: false,
              canMove: false,
              canResize: false,
              itemProps: {
                style: {
                  textAlign: "center" as const,
                  justifyContent: "center",
                },
              },
            });
          }
        }
      }
      setUnassignedItems(laned);
      setItems([...roomShiftItems, ...orderBars]);
      setLoading(false);
      return;
    }
    const items = stateOrders
      .filter(
        (order) =>
          EnumUtils.mapToEnum(OrderStatus, order.status) !==
            OrderStatus.cancel &&
          (order.departureTime || order.planInTime) &&
          order.courseTime
      )
      .map((order) => ({
        id: `order-${order.orderId}`,
        group: order?.castNameId ? `castName-${order.castNameId}` : 0,
        title: `${order.guest?.name || "ゲスト未設定"}(${
          order.hotel?.name || ""
        })`,
        color: itemColor(order),
        textColor: order?.isSentEmailToCast ? "red" : "",
        start_time: order?.actualInTime
          ? Number(DateTime.fromJSDate(order.actualInTime).toFormat("x"))
          : Number(
              DateTime.fromJSDate(
                order.departureTime || order.planInTime || new Date()
              ).toFormat("x")
            ),
        end_time: Number(
          order.actualEndTime
            ? DateTime.fromJSDate(order.actualEndTime).toFormat("x")
            : order.planOutTime
            ? DateTime.fromJSDate(order.planOutTime).toFormat("x")
            : DateTime.fromJSDate(
                order.departureTime || order.planInTime || new Date()
              )
                .plus({ minutes: order.courseTime || 0 })
                .toFormat("x")
        ),
        canChangeGroup: true,
        canMove: true,
        canResize: "right",
      }));
    const copyItems = stateOrders
      .filter(
        (order) =>
          EnumUtils.mapToEnum(OrderStatus, order.status) !==
            OrderStatus.cancel &&
          (order.departureTime || order.planInTime) &&
          order.courseTime
      )
      .flatMap((order) => {
        const cast = castShifts.find((castShift) =>
          castShift.cast?.castNames
            .map((castName) => castName?.castNameId)
            .includes(order?.castNameId || 0)
        )?.cast;
        if (!cast) return;
        return cast.castNames.flatMap((castName) => {
          if (castName.castNameId === order.castNameId) return;
          return {
            id: `copyOrder-${order.orderId}-${castName.castNameId}`,
            group: `castName-${castName.castNameId}`,
            title: `${order.shop?.name || ""} ${order.castName?.name || ""}`,
            color: "#949494",
            start_time: order?.actualInTime
              ? Number(DateTime.fromJSDate(order.actualInTime).toFormat("x"))
              : Number(
                  DateTime.fromJSDate(
                    order.departureTime || order.planInTime || new Date()
                  ).toFormat("x")
                ),
            end_time: Number(
              order.actualEndTime
                ? DateTime.fromJSDate(order.actualEndTime).toFormat("x")
                : order.planOutTime
                ? DateTime.fromJSDate(order.planOutTime).toFormat("x")
                : DateTime.fromJSDate(
                    order.departureTime || order.planInTime || new Date()
                  )
                    .plus({ minutes: order.courseTime || 0 })
                    .toFormat("x")
            ),
            canChangeGroup: false,
            canMove: false,
            canResize: false,
          };
        });
      })
      .filter(Boolean);
    const allItems = [...items, ...copyItems];
    const shift = stateShifts
      .flatMap((shift) => {
        return shift.cast?.castNames?.flatMap((castName) => ({
          id: `shift-${shift.castShiftId}-${castName.castNameId}`,
          group: `castName-${castName.castNameId}`,
          title: `${
            EnumUtils.mapToEnum(ShiftStatus, shift.status) ===
            ShiftStatus.absence
              ? "当日欠勤"
              : shift.actualWorkStart
              ? "実出勤"
              : "出勤予定"
          }`,
          color: shift.scheduleItemColor
            ? shift.scheduleItemColor
            : shiftColor(shift.status),
          start_time: shiftStartTime(shift, castName.shop),
          end_time: shiftEndTime(shift, castName.shop),
          castShift: shift,
          canChangeGroup: false,
          canMove: true,
          canResize: "both",
        }));
      })
      .filter(Boolean);
    const rests = stateShifts
      .flatMap((shift) => {
        if (shift.workOffStart && shift.workOffEnd) {
          return shift.cast?.castNames?.flatMap((castName) => ({
            id: `rest-${shift.castShiftId}-${castName.castNameId}`,
            group: `castName-${castName.castNameId}`,
            title: "休憩",
            color: "#949494",
            start_time: Number(
              DateTime.fromJSDate(shift.workOffStart || new Date()).toFormat(
                "x"
              )
            ),
            end_time: Number(
              DateTime.fromJSDate(shift.workOffEnd || new Date()).toFormat("x")
            ),
            castShift: shift,
            canChangeGroup: false,
            canMove: false,
            canResize: false,
          }));
        }
      })
      .filter(Boolean);
    const allShift = [...shift, ...rests];
    const notBusinessHoursBefore = stateShifts
      .flatMap((shift) => {
        return shift.cast?.castNames.flatMap((castName) => {
          if (!castName.shop) return;
          return {
            id: `businessHourBefore-${castName.castNameId}`,
            group: `castName-${castName.castNameId}`,
            title: "営業時間外",
            start_time: Number(
              DateTime.fromJSDate(startDate).startOf("day").toFormat("x")
            ),
            end_time: Number(
              DateTime.fromJSDate(startDate)
                .startOf("day")
                .plus({
                  hours: Number(castName.shop.openingTime.split(":")[0]),
                })
                .toFormat("x")
            ),
            canChangeGroup: false,
            canMove: false,
            canResize: false,
          };
        });
      })
      .filter(Boolean);
    const notBusinessHoursAfter = stateShifts
      .flatMap((shift) => {
        return shift.cast?.castNames.flatMap((castName) => {
          if (!castName.shop) return;
          return {
            id: `businessHourAfter-${castName.castNameId}`,
            group: `castName-${castName.castNameId}`,
            title: "営業時間外",
            start_time: Number(
              DateTime.fromJSDate(startDate)
                .startOf("day")
                .plus({
                  hours:
                    Number(castName.shop.closingTime.split(":")[0]) < 12
                      ? Number(castName.shop.closingTime.split(":")[0]) + 25
                      : Number(castName.shop.closingTime.split(":")[0]) + 1,
                })
                .toFormat("x")
            ),
            end_time: Number(
              DateTime.fromJSDate(endDate).endOf("day").toFormat("x")
            ),
            canChangeGroup: false,
            canMove: false,
            canResize: false,
          };
        });
      })
      .filter(Boolean);
    const notBusinessHours = [
      ...notBusinessHoursBefore,
      ...notBusinessHoursAfter,
    ];
    setItems([...allItems, ...allShift, ...notBusinessHours]);
    setLoading(false);
  }, [stateShifts, stateOrders, isVisit]);

  const shiftStartTime = (shift: CastShiftRes, shop: ShopRes) => {
    if (!shop) return;
    if (shift.isForceShift) {
      return Number(
        DateTime.fromJSDate(
          shift?.actualWorkStart || shift?.planWorkStart || new Date()
        ).toFormat("x")
      );
    }
    return Number(
      DateTime.fromJSDate(
        shift?.actualWorkStart || shift?.planWorkStart || new Date()
      ).toFormat("x") <
        DateTime.fromJSDate(startDate)
          .startOf("day")
          .plus({
            hours: Number(shop.openingTime.split(":")[0]),
          })
          .toFormat("x")
        ? DateTime.fromJSDate(startDate)
            .startOf("day")
            .plus({
              hours: Number(shop.openingTime.split(":")[0]),
            })
            .toFormat("x")
        : DateTime.fromJSDate(
            shift?.actualWorkStart || shift?.planWorkStart || new Date()
          ).toFormat("x")
    );
  };

  const shiftEndTime = (shift: CastShiftRes, shop: ShopRes) => {
    if (!shop) return;
    if (shift.isForceShift) {
      return Number(
        DateTime.fromJSDate(
          shift?.actualWorkEnd || shift?.planWorkEnd || new Date()
        ).toFormat("x")
      );
    }
    return Number(
      DateTime.fromJSDate(
        shift?.actualWorkEnd || shift?.planWorkEnd || new Date()
      ).toFormat("x") >
        DateTime.fromJSDate(startDate)
          .startOf("day")
          .plus({
            hours:
              Number(shop.closingTime.split(":")[0]) < 12
                ? Number(shop.closingTime.split(":")[0]) + 25
                : Number(shop.closingTime.split(":")[0]) + 1,
          })
          .toFormat("x")
        ? DateTime.fromJSDate(startDate)
            .startOf("day")
            .plus({
              hours:
                Number(shop.closingTime.split(":")[0]) < 12
                  ? Number(shop.closingTime.split(":")[0]) + 25
                  : Number(shop.closingTime.split(":")[0]) + 1,
            })
            .toFormat("x")
        : DateTime.fromJSDate(
            shift?.actualWorkEnd || shift?.planWorkEnd || new Date()
          ).toFormat("x")
    );
  };

  const itemColor = (order: OrderRes) => {
    if (EnumUtils.mapToEnum(OrderStatus, order.status) === OrderStatus.paid) {
      return "#93c47d";
    }
    if (EnumUtils.mapToEnum(OrderStatus, order.status) === OrderStatus.hold) {
      return order?.planOutTime && order.planOutTime > new Date()
        ? "#ffe599"
        : "#d5a6bd";
    }
    if (
      EnumUtils.mapToEnum(OrderStatus, order.status) === OrderStatus.booking
    ) {
      if (order?.requestActualInTime && !order?.actualInTime) {
        return "#f53737";
      }
      return order?.planOutTime && order.planOutTime > new Date()
        ? "#6d9eeb"
        : "#ea9999";
    }
  };

  const shiftColor = (shiftStatus: ShiftStatus) => {
    switch (EnumUtils.mapToEnum(ShiftStatus, shiftStatus)) {
      case ShiftStatus.unconfirmed:
        return "#e8dc45";
      case ShiftStatus.verified:
        return "#30c91c";
      case ShiftStatus.tardy:
        return "#800080";
      case ShiftStatus.absence:
        return "#949494";
    }
  };

  const onTimeChange = (
    visibleTimeStart: number,
    visibleTimeEnd: number,
    updateScrollCanvas: (start: number, end: number) => void
  ) => {
    const minTime = Number(DateTime.fromJSDate(minDate).toFormat("x"));
    const maxTime = Number(DateTime.fromJSDate(maxDate).toFormat("x"));
    if (visibleTimeStart < minTime && visibleTimeEnd > maxTime) {
      updateScrollCanvas(minTime, maxTime);
    } else if (visibleTimeStart < minTime) {
      updateScrollCanvas(
        minTime,
        minTime + (visibleTimeEnd - visibleTimeStart)
      );
    } else if (visibleTimeEnd > maxTime) {
      updateScrollCanvas(
        maxTime - (visibleTimeEnd - visibleTimeStart),
        maxTime
      );
    }
  };

  // visit (来店型) 部屋バーから「未アサインに戻す」 ハンドラ。
  // 誤操作防止のため pendingRoomMove ダイアログ (確定/キャンセル) を開いて確認させる。
  const handleUnassign = (item: any) => {
    const shift = item?.castShift;
    if (!shift) return;
    if (shift.roomId == null) return;
    const castName =
      shift.cast?.castNames?.[0]?.name ||
      shift.cast?.displayName ||
      "キャスト";
    const labelOfRoom = (rid: number | null) => {
      if (rid == null) return "未アサイン";
      const r = (rooms || []).find((x: any) => x.roomId === rid);
      return r ? r.name : `部屋#${rid}`;
    };
    const fmt = (d: Date) =>
      DateTime.fromJSDate(d).toFormat("M/d HH:mm");
    setPendingRoomMove({
      shiftId: shift.castShiftId,
      castDisplayName: castName,
      fromLabel: `${labelOfRoom(shift.roomId)} ${fmt(
        shift.planWorkStart
      )}〜${fmt(shift.planWorkEnd)}`,
      toLabel: `未アサイン ${fmt(shift.planWorkStart)}〜${fmt(
        shift.planWorkEnd
      )}`,
      newRoomId: null,
      originalRoomId: shift.roomId,
      originalPlanWorkStart: shift.planWorkStart,
      originalPlanWorkEnd: shift.planWorkEnd,
      newPlanWorkStart: shift.planWorkStart,
      newPlanWorkEnd: shift.planWorkEnd,
    });
  };

  const onItemMove = (
    itemId: string,
    dragTime: number,
    newGroupOrder: number
  ) => {
    // visit (来店型): castShift の room_id を新 group の room id に更新。
    // 確定はポップアップで user が押した時点で行うため、ここでは pendingRoomMove に積むだけ。
    if (isVisit) {
      if (itemId.split("-")[0] !== "shift") return;
      const newGroup = groups[newGroupOrder];
      if (!newGroup) return;
      if (newGroup.root) {
        return alert("店舗仕切り行には移動できません。部屋に直接移動してください。");
      }
      const groupIdStr = String(newGroup.id);
      let newRoomId: number | null = null;
      if (groupIdStr === "0") {
        newRoomId = null;
      } else if (groupIdStr.startsWith("room-")) {
        newRoomId = Number(groupIdStr.split("-")[1]);
      } else {
        return;
      }
      const shiftId = Number(itemId.split("-")[1]);
      // stateShifts (確定済を含む最新) を参照。
      // castShifts (redux) は次回 fetchCastShift まで古いままなので NG。
      const castShift: any = (stateShifts.length ? stateShifts : castShifts).find(
        (s: any) => s.castShiftId === shiftId
      );
      if (!castShift) return;
      // 同じ部屋 & 同じ開始時刻ならスキップ (誤クリック保護)
      const sameRoom = (castShift.roomId ?? null) === newRoomId;
      const sameTime =
        castShift.planWorkStart &&
        DateTime.fromJSDate(castShift.planWorkStart).toMillis() === dragTime;
      if (sameRoom && sameTime) return;
      const diff = DateTime.fromJSDate(castShift.planWorkEnd).diff(
        DateTime.fromJSDate(castShift.planWorkStart),
        "minutes"
      ).minutes;
      const newPlanWorkStart = DateTime.fromMillis(dragTime).toJSDate();
      const newPlanWorkEnd = DateTime.fromMillis(dragTime)
        .plus({ minutes: diff })
        .toJSDate();
      const labelOfRoom = (rid: number | null): string => {
        if (rid === null) return "未アサイン";
        const r = (rooms || []).find((x: any) => x.roomId === rid);
        return r ? `${r.name}` : `部屋#${rid}`;
      };
      const fmt = (d: Date) =>
        DateTime.fromJSDate(d).toFormat("M/d HH:mm");
      setPendingRoomMove({
        shiftId,
        castDisplayName:
          castShift.cast?.displayName ||
          castShift.cast?.castNames?.[0]?.name ||
          "キャスト",
        fromLabel: `${labelOfRoom(
          castShift.roomId ?? null
        )} ${fmt(castShift.planWorkStart)}〜${fmt(castShift.planWorkEnd)}`,
        toLabel: `${labelOfRoom(newRoomId)} ${fmt(newPlanWorkStart)}〜${fmt(
          newPlanWorkEnd
        )}`,
        newRoomId,
        originalRoomId: castShift.roomId ?? null,
        originalPlanWorkStart: castShift.planWorkStart,
        originalPlanWorkEnd: castShift.planWorkEnd,
        newPlanWorkStart,
        newPlanWorkEnd,
      });
      return;
    }
    if (itemId.split("-")[0] === "order") {
      if (newGroupOrder === 0) return alert("未アサインには移動できません。");
      const group = groups[newGroupOrder];
      if (group.root) return alert("店舗には移動できません。");
      const defaultOrders: any[] = orders.map((order) => ({ ...order }));
      const order = defaultOrders.find(
        (order: OrderRes) => order.orderId === Number(itemId.split("-")[1])
      );
      if (!order) return;
      const castNameId = Number(group.id.split("-")[1]);
      const castName = castShifts
        .find((castShift) =>
          castShift.cast.castNames.find(
            (castName) => castName.castNameId === castNameId
          )
        )
        ?.cast?.castNames?.find(
          (castName) => castName.castNameId === castNameId
        );
      if (!castName) return;
      if (order?.castName?.notelClassId !== castName.notelClassId) {
        return setShowMoveChangeClassForm(true, {
          type: "castScheduleOnItemMoveChangeClass",
          order,
          castName,
        });
      }
      if (order.departureTime && order.planInTime) {
        const diffTIme = DateTime.fromJSDate(order.planInTime).diff(
          DateTime.fromJSDate(order.departureTime),
          "minutes"
        ).minutes;
        order.departureTime = DateTime.fromMillis(dragTime).toJSDate();
        order.planInTime = DateTime.fromMillis(dragTime)
          .plus({ minutes: diffTIme })
          .toJSDate();
      } else if (order.departureTime) {
        order.departureTime = DateTime.fromMillis(dragTime).toJSDate();
        order.planInTime = null;
      } else if (order.planInTime) {
        order.departureTime = null;
        order.planInTime = DateTime.fromMillis(dragTime).toJSDate();
      }
      order.planOutTime = DateTime.fromJSDate(
        order.planInTime || order.departureTime
      )
        .plus({ minutes: order.totalTime || 0 })
        .toJSDate();
      order.castNameId = castNameId;
      setStateOrders([
        ...defaultOrders.map((item: OrderRes) =>
          item.orderId === order.orderId ? order : item
        ),
      ]);
      setShowMoveForm(true, {
        type: "castScheduleOnItemMove",
        order,
        castName,
      });
    } else {
      const shiftId = Number(itemId.split("-")[1]);
      const defaultShifts = castShifts.map((shift) => ({ ...shift }));
      const castShift: any = defaultShifts.find(
        (shift) => shift.castShiftId === shiftId
      );
      if (!castShift) return;
      const diff = DateTime.fromJSDate(
        castShift?.actualWorkEnd
          ? castShift.actualWorkEnd
          : castShift.planWorkEnd
      ).diff(
        DateTime.fromJSDate(
          castShift?.actualWorkStart
            ? castShift.actualWorkStart
            : castShift.planWorkStart
        ),
        "minutes"
      ).minutes;
      if (castShift?.actualWorkEnd) {
        castShift.actualWorkEnd = DateTime.fromMillis(dragTime)
          .plus({ minutes: diff })
          .toJSDate();
      } else {
        castShift.planWorkEnd = DateTime.fromMillis(dragTime)
          .plus({ minutes: diff })
          .toJSDate();
      }

      if (castShift?.actualWorkStart) {
        castShift.actualWorkStart = DateTime.fromMillis(dragTime).toJSDate();
      } else {
        castShift.planWorkStart = DateTime.fromMillis(dragTime).toJSDate();
      }

      setStateShifts([
        ...defaultShifts.map((shift) =>
          shift.castShiftId === castShift.castShiftId ? castShift : shift
        ),
      ]);
      setShowShiftMoveForm(true, {
        type: "castScheduleOnShiftMove",
        castShift,
      });
    }
  };

  const onItemResize = (
    itemId: string,
    time: number,
    edge: "left" | "right"
  ) => {
    if (itemId.split("-")[0] === "order") {
      const defaultOrders: any[] = orders.map((order) => ({ ...order }));
      const order = defaultOrders.find(
        (order: OrderRes) => order.orderId === Number(itemId.split("-")[1])
      );
      if (!order) return;
      if (edge === "right") {
        order.planOutTime = DateTime.fromMillis(time).toJSDate();
      } else {
        order.planInTime = DateTime.fromMillis(time).toJSDate();
      }
      setStateOrders([
        ...defaultOrders.map((item: OrderRes) =>
          item.orderId === order.orderId ? order : item
        ),
      ]);
      setShowResizeForm(true, {
        type: "castScheduleOnItemResize",
        order,
      });
    } else {
      const shiftId = Number(itemId.split("-")[1]);
      const defaultShifts = castShifts.map((shift) => ({ ...shift }));
      const castShift: any = defaultShifts.find(
        (shift) => shift.castShiftId === shiftId
      );
      if (!castShift) return;
      if (castShift?.actualWorkEnd) {
        if (edge === "right") {
          castShift.actualWorkEnd = DateTime.fromMillis(time).toJSDate();
        } else {
          castShift.actualWorkStart = DateTime.fromMillis(time).toJSDate();
        }
      } else {
        if (edge === "right") {
          castShift.planWorkEnd = DateTime.fromMillis(time).toJSDate();
        } else {
          castShift.planWorkStart = DateTime.fromMillis(time).toJSDate();
        }
      }
      setStateShifts([
        ...defaultShifts.map((shift) =>
          shift.castShiftId === castShift.castShiftId ? castShift : shift
        ),
      ]);
      setShowShiftMoveForm(true, {
        type: "castScheduleOnShiftMove",
        castShift,
      });
    }
  };

  const onCanvasClick = (
    groupId: string,
    time: number,
    e: React.SyntheticEvent
  ) => {
    const order = {} as CreateOrderReq;
    order.departureTime = DateTime.fromMillis(time).toJSDate();
    order.planInTime = DateTime.fromMillis(time).toJSDate();
    order.planOutTime = DateTime.fromMillis(time)
      .plus({ minutes: 60 })
      .toJSDate();
    order.orderDate = DateTime.fromMillis(time).toJSDate();
    order.status = OrderStatus.hold;
    if (groupId.split("-")[0] !== "castName") {
      return setShowCreateNoCastForm(true, {
        type: "castScheduleOnItemCreateNoCast",
        order,
      });
    }
    const castNameId = Number(groupId.split("-")[1]);
    const castName = castShifts
      .find((castShift) =>
        castShift.cast.castNames
          .map((castName) => castName.castNameId)
          .includes(castNameId)
      )
      ?.cast?.castNames?.find((castName) => castName.castNameId === castNameId);
    if (!castName) return;
    order.castNameId = castName.castNameId;
    ReactGA.event({
      action: `canvasDoubleClick-${castName.companyId}`,
      category: `castSchedule`,
      value: 1,
    });
    setShowCreateForm(true, {
      type: "castScheduleOnItemCreate",
      order,
      castName,
    });
  };

  const itemRenderer = (
    item: any,
    itemContext: any,
    getItemProps: any,
    getResizeProps: any
  ) => {
    if (item.id.split("-")[0] === "shift" || item.id.split("-")[0] === "rest") {
      return (
        <CastScheduleShiftItem
          item={item}
          getItemProps={getItemProps}
          itemContext={itemContext}
          onClick={() => onClickEditForm(item)}
          isVisit={isVisit}
          onUnassign={() => handleUnassign(item)}
        />
      );
    }
    if (
      item.id.split("-")[0] === "businessHourBefore" ||
      item.id.split("-")[0] === "businessHourAfter"
    ) {
      return (
        <Item {...getItemProps(item.itemProps)} color={item.color}>
          <div
            className="rct-item-content"
            style={{ maxHeight: `${itemContext.dimensions.height}` }}
          >
            {itemContext.title}
          </div>
        </Item>
      );
    }
    const { left: leftResizeProps, right: rightResizeProps } = getResizeProps();
    const order = orders.find(
      (order) => order.orderId === Number(item.id.split("-")[1])
    );
    if (!order) return <></>;
    return (
      <CastScheduleOrderItem
        order={order}
        item={item}
        itemContext={itemContext}
        getItemProps={getItemProps}
        rightResizeProps={rightResizeProps}
        leftResizeProps={leftResizeProps}
      />
    );
  };

  const onClickCreateForm = () => {
    setCastForm(true, { type: "addData", item: { status: "unconfirmed" } });
  };

  const onClickEditForm = (item: any) => {
    setCastForm(true, { type: "editData", item: item.castShift });
  };

  const onClickReload = () => {
    dispatch(
      fetchCastShift(
        companyId,
        DateTime.fromJSDate(startDate).toFormat(FORMAT_TYPE.YEAR_DAY),
        DateTime.fromJSDate(endDate).toFormat(FORMAT_TYPE.YEAR_DAY)
      )
    );
    dispatch(
      fetchOrder(
        companyId,
        DateTime.fromJSDate(startDate).toFormat(FORMAT_TYPE.YEAR_DAY),
        DateTime.fromJSDate(endDate)
          .plus({ days: TimeUtils.isInAllDays(shops) ? 1 : 0 })
          .toFormat(FORMAT_TYPE.YEAR_DAY)
      )
    );
  };

  if (loading) return <CircularProgress />;
  // visit (来店型) sticky overlay の高さ計算 + TimelineHeaders の sticky top に流用。
  // 0 件なら overlay 自体非表示 (TimelineHeaders は top:0 に戻す)。
  const visitOverlayHeight =
    isVisit && unassignedItems.length > 0
      ? (Math.max(0, ...unassignedItems.map((i) => i.lane)) + 1) * 28 + 12
      : 0;
  return (
    <>
      <Box display="flex" flexDirection="column">
        <Box margin={2} display="flex" alignItems="center">
          <Box style={{ marginRight: 16 }}>
            <DayNav
              selectedDate={DateTime.fromJSDate(minDate)}
              onChange={(d) => {
                const datetime = d
                  .startOf("day")
                  .plus({ hours: startTime })
                  .toJSDate();
                const nextDatetime = DateTime.fromJSDate(datetime)
                  .startOf("day")
                  .plus({ hours: endTime })
                  .toJSDate();
                setMinDate(datetime);
                setStartDate(datetime);
                setMaxDate(nextDatetime);
                setEndDate(nextDatetime);
                dispatch(
                  fetchOrder(
                    companyId,
                    DateTime.fromJSDate(datetime).toFormat(FORMAT_TYPE.YEAR_DAY),
                    DateTime.fromJSDate(nextDatetime)
                      .plus({ days: TimeUtils.isInAllDays(shops) ? 1 : 0 })
                      .toFormat(FORMAT_TYPE.YEAR_DAY)
                  )
                );
                dispatch(
                  fetchCastShift(
                    companyId,
                    DateTime.fromJSDate(datetime).toFormat(FORMAT_TYPE.YEAR_DAY),
                    DateTime.fromJSDate(nextDatetime).toFormat(
                      FORMAT_TYPE.YEAR_DAY
                    )
                  )
                );
              }}
            />
          </Box>
          <Box>
            <InputLabel id="select-multiple-shop">店舗</InputLabel>
            <Select
              multiple
              value={selectShops}
              onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                setSelectShops((prev: string[]) => {
                  if (
                    prev.includes("すべて") &&
                    (event.target.value as string[]).indexOf("すべて") === -1
                  ) {
                    return [];
                  } else if (
                    !prev.includes("すべて") &&
                    (event.target.value as string[]).indexOf("すべて") !== -1
                  ) {
                    return [
                      ...shops.map((shop) => String(shop.shopId)),
                      "すべて",
                    ];
                  } else if (
                    (event.target.value as string[]).length === shops.length &&
                    (event.target.value as string[]).indexOf("すべて") === -1
                  ) {
                    return [
                      ...shops.map((shop) => String(shop.shopId)),
                      "すべて",
                    ];
                  } else if (
                    (event.target.value as string[]).length <= shops.length
                  ) {
                    return (event.target.value as string[]).filter(
                      (name) => name !== "すべて"
                    );
                  } else {
                    return event.target.value as string[];
                  }
                });
              }}
              input={<Input id="select-multiple-shop" />}
              style={{ width: "200px", marginRight: "10px" }}
              renderValue={(selected) => {
                if ((selected as string[]).includes("すべて")) {
                  return "すべて";
                } else {
                  return shops
                    .filter((shop) =>
                      (selected as string[]).includes(String(shop.shopId))
                    )
                    .map((shop) => shop.name)
                    .join(", ");
                }
              }}
            >
              <MenuItem key={"すべて"} value={"すべて"}>
                <Checkbox
                  name="all"
                  checked={selectShops.indexOf("すべて") > -1}
                />
                <ListItemText primary={"すべて"} />
              </MenuItem>
              {shops.map((shop) => (
                <MenuItem key={shop.shopId} value={String(shop.shopId)}>
                  <Checkbox
                    checked={selectShops.indexOf(String(shop.shopId)) !== -1}
                  />
                  <ListItemText primary={shop.name} />
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box>
            <InputLabel id="select-multiple-notel-class">クラス</InputLabel>
            <Select
              multiple
              value={selectNotelClasses}
              onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                setSelectNotelClasses((prev: string[]) => {
                  if (
                    prev.includes("すべて") &&
                    (event.target.value as string[]).indexOf("すべて") === -1
                  ) {
                    return [];
                  } else if (
                    !prev.includes("すべて") &&
                    (event.target.value as string[]).indexOf("すべて") !== -1
                  ) {
                    return [
                      ...notelClasses.map((notelClass) =>
                        String(notelClass.notelClassId)
                      ),
                      "すべて",
                    ];
                  } else if (
                    (event.target.value as string[]).length ===
                      notelClasses.length &&
                    (event.target.value as string[]).indexOf("すべて") === -1
                  ) {
                    return [
                      ...notelClasses.map((notelClass) =>
                        String(notelClass.notelClassId)
                      ),
                      "すべて",
                    ];
                  } else if (
                    (event.target.value as string[]).length <=
                    notelClasses.length
                  ) {
                    return (event.target.value as string[]).filter(
                      (name) => name !== "すべて"
                    );
                  } else {
                    return event.target.value as string[];
                  }
                });
              }}
              input={<Input id="select-multiple-notelClass" />}
              style={{ width: "200px", marginRight: "10px" }}
              renderValue={(selected) => {
                if ((selected as string[]).includes("すべて")) {
                  return "すべて";
                } else {
                  return notelClasses
                    .filter((notelClass) =>
                      (selected as string[]).includes(
                        String(notelClass.notelClassId)
                      )
                    )
                    .map((notelClass) => notelClass.name)
                    .join(", ");
                }
              }}
            >
              <MenuItem key={"すべて"} value={"すべて"}>
                <Checkbox
                  name="all"
                  checked={selectNotelClasses.indexOf("すべて") > -1}
                />
                <ListItemText primary={"すべて"} />
              </MenuItem>
              {notelClasses.map((notelClass) => (
                <MenuItem
                  key={notelClass.notelClassId}
                  value={String(notelClass.notelClassId)}
                >
                  <Checkbox
                    checked={
                      selectNotelClasses.indexOf(
                        String(notelClass.notelClassId)
                      ) !== -1
                    }
                  />
                  <ListItemText primary={notelClass.name} />
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box>
            <InputLabel id="select-sort">並び順</InputLabel>
            <Select
              value={sort}
              onChange={(event) => {
                setSort(String(event.target.value));
              }}
              input={<Input id="select-sort" />}
              style={{ width: "200px", marginRight: "10px" }}
            >
              <MenuItem key={"class"} value={"class"}>
                <ListItemText primary={"クラス順"} />
              </MenuItem>
              <MenuItem key={"time"} value={"time"}>
                <ListItemText primary={"時間順"} />
              </MenuItem>
            </Select>
          </Box>
          <TextField
            value={filterCastName}
            onChange={(event) => {
              setFilterCastName(event.target.value);
            }}
            label="キャスト名絞り込み"
            style={{ margin: "10px", width: "200px" }}
          />
          <Box p={1} display="flex">
            <Button
              variant="contained"
              className={classes.button}
              endIcon={<Icon>add</Icon>}
              color="secondary"
              onClick={() => onClickCreateForm()}
            >
              追加
            </Button>
          </Box>
          <Box p={1} display="flex">
            <Button
              variant="contained"
              className={classes.button}
              color="primary"
              onClick={() => onClickReload()}
            >
              データ更新
            </Button>
          </Box>
        </Box>
      </Box>
      <div style={{ width: "100vw" }}>
        {isVisit && unassignedItems.length > 0 && (
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 1250,
              background: "#f6f9fc",
              borderBottom: "2px solid #1976d2",
              display: "flex",
              height: visitOverlayHeight,
            }}
          >
            <div
              style={{
                width: 300,
                flex: "0 0 300px",
                padding: "6px 12px",
                borderRight: "1px solid #ccc",
                fontWeight: 600,
                color: "#1976d2",
                fontSize: 14,
                background: "#eef3f9",
              }}
            >
              未アサイン ({unassignedItems.length})
              <div
                style={{
                  fontSize: 11,
                  color: "#666",
                  fontWeight: 400,
                  marginTop: 2,
                }}
              >
                ドラッグで部屋に振り分け
              </div>
            </div>
            <div style={{ flex: 1, position: "relative" }}>
              {unassignedItems.map((item) => {
                const visibleStart = DateTime.fromJSDate(startDate).toMillis();
                const visibleEnd = DateTime.fromJSDate(endDate)
                  .plus({ days: 1 })
                  .toMillis();
                const span = visibleEnd - visibleStart;
                const left =
                  ((item.start_time - visibleStart) / span) * canvasWidth;
                const width = Math.max(
                  40,
                  ((item.end_time - item.start_time) / span) * canvasWidth
                );
                return (
                  <Tooltip
                    key={item.id}
                    trigger="hover"
                    placement="top"
                    overlayStyle={{ zIndex: 1400 }}
                    overlay={
                      <Box
                        display="flex"
                        flexDirection="column"
                        style={{ maxWidth: 320 }}
                      >
                        <span style={{ whiteSpace: "pre-wrap" }}>
                          キャスト備考：
                          {item.castShift?.cast?.remarks || "未設定"}
                        </span>
                        <span>
                          キャストメモ：
                          {item.castShift?.castMemo || "未設定"}
                        </span>
                        <span>
                          スタッフメモ：
                          {item.castShift?.staffMemo || "未設定"}
                        </span>
                      </Box>
                    }
                  >
                    <div
                      draggable
                      onDragStart={(e) => {
                        setDraggingShiftId(item.shiftId);
                        e.dataTransfer.setData(
                          "text/plain",
                          String(item.shiftId)
                        );
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => setDraggingShiftId(null)}
                      style={{
                        position: "absolute",
                        left,
                        width,
                        top: item.lane * 28 + 4,
                        height: 24,
                        background: item.color,
                        color: "white",
                        fontSize: 12,
                        fontWeight: 500,
                        borderRadius: 4,
                        padding: "0 6px",
                        cursor: "grab",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        opacity:
                          draggingShiftId === item.shiftId ? 0.4 : 1,
                        userSelect: "none",
                        display: "flex",
                        alignItems: "center",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                      }}
                    >
                      {item.title}
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        )}
        <div
          ref={timelineWrapperRef}
          onDragOver={
            isVisit
              ? (e) => {
                  if (draggingShiftId == null) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }
              : undefined
          }
          onDrop={
            isVisit
              ? (e) => {
                  e.preventDefault();
                  const shiftIdStr = e.dataTransfer.getData("text/plain");
                  if (!shiftIdStr) return;
                  const shiftId = Number(shiftIdStr);
                  if (!timelineWrapperRef.current) return;
                  const rect =
                    timelineWrapperRef.current.getBoundingClientRect();
                  const SIDEBAR = 300;
                  const HEADER = 52;
                  const relX = e.clientX - rect.left;
                  const relY = e.clientY - rect.top;
                  if (relX < SIDEBAR || relY < HEADER) return;
                  const visibleStart =
                    DateTime.fromJSDate(startDate).toMillis();
                  const visibleEnd = DateTime.fromJSDate(endDate)
                    .plus({ days: 1 })
                    .toMillis();
                  const span = visibleEnd - visibleStart;
                  const cW = rect.width - SIDEBAR;
                  const dropTime =
                    visibleStart + ((relX - SIDEBAR) / cW) * span;
                  const canvasY = relY - HEADER;
                  let acc = 0;
                  let targetGroup: any = null;
                  for (const g of groups) {
                    const next = acc + (g.height || 0);
                    if (canvasY >= acc && canvasY < next) {
                      targetGroup = g;
                      break;
                    }
                    acc = next;
                  }
                  if (!targetGroup) return;
                  if (targetGroup.root) {
                    alert(
                      "店舗仕切り行には移動できません。 部屋に直接ドロップしてください。"
                    );
                    return;
                  }
                  const gid = String(targetGroup.id);
                  if (!gid.startsWith("room-")) return;
                  const newRoomId = Number(gid.split("-")[1]);
                  const u = unassignedItems.find(
                    (i) => i.shiftId === shiftId
                  );
                  if (!u) return;
                  // 部屋へのアサイン時はキャストの出勤時刻 (planWorkStart/End) は変えず、
                  // 部屋だけを変更する (マウス位置で時刻が動くのは違和感)
                  const newPlanWorkStart = u.planWorkStart;
                  const newPlanWorkEnd = u.planWorkEnd;
                  const labelOfRoom = (rid: number | null) => {
                    if (rid == null) return "未アサイン";
                    const r = (rooms || []).find(
                      (x: any) => x.roomId === rid
                    );
                    return r ? r.name : `部屋#${rid}`;
                  };
                  const fmt = (d: Date) =>
                    DateTime.fromJSDate(d).toFormat("M/d HH:mm");
                  setPendingRoomMove({
                    shiftId,
                    castDisplayName: u.castDisplayName,
                    fromLabel: `未アサイン ${fmt(u.planWorkStart)}〜${fmt(
                      u.planWorkEnd
                    )}`,
                    toLabel: `${labelOfRoom(newRoomId)} ${fmt(
                      newPlanWorkStart
                    )}〜${fmt(newPlanWorkEnd)}`,
                    newRoomId,
                    originalRoomId: null,
                    originalPlanWorkStart: u.planWorkStart,
                    originalPlanWorkEnd: u.planWorkEnd,
                    newPlanWorkStart,
                    newPlanWorkEnd,
                  });
                }
              : undefined
          }
        >
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Timeline
            groups={groups}
            items={items}
            keys={keys}
            sidebarWidth={isVisit ? 300 : 150}
            itemHeightRatio={isVisit ? 0.7 : 0.65}
            visibleTimeStart={DateTime.fromJSDate(startDate).toMillis()}
            visibleTimeEnd={DateTime.fromJSDate(endDate)
              .plus({ days: 1 })
              .toMillis()}
            stackItems
            onTimeChange={onTimeChange}
            onItemResize={(itemId, time, edge) =>
              onItemResize(itemId as string, time, edge)
            }
            onItemMove={(itemId, dragTime, newGroupOrder) =>
              onItemMove(itemId as string, dragTime, newGroupOrder)
            }
            itemRenderer={({
              item,
              itemContext,
              getItemProps,
              getResizeProps,
            }) => itemRenderer(item, itemContext, getItemProps, getResizeProps)}
            onCanvasDoubleClick={(groupId, time, e) =>
              onCanvasClick(groupId as string, time, e)
            }
            horizontalLineClassNamesForGroup={(group) =>
              group.root ? ["rct-row-root"] : []
            }
            groupRenderer={({ group }) => (
              <div className={group.root ? "rct-row-root" : ""}>
                <div className="title">
                  <Typography style={{ whiteSpace: "pre-wrap" }}>
                    {group.title}
                  </Typography>
                </div>
              </div>
            )}
          >
            <TodayMarker date={DateTime.local().toJSDate()}>
              {({ styles }) => (
                <div style={{ ...styles, backgroundColor: "red" }} />
              )}
            </TodayMarker>
            <TimelineHeaders
              style={{
                position: "sticky",
                top: visitOverlayHeight,
                zIndex: 1200,
              }}
            >
              <DateHeader
                unit="day"
                labelFormat="YYYY年MM月DD日"
                style={{ pointerEvents: "none" }}
              />
              <DateHeader
                unit="hour"
                labelFormat="HH:00"
                style={{ pointerEvents: "none" }}
              />
            </TimelineHeaders>
          </Timeline>
        )}
        </div>
        {castScheduleOnItemMove?.show && (
          <CastScheduleOnItemMove
            setStateOrders={setStateOrders}
            orders={[...orders]}
          />
        )}
        {castScheduleOnItemMoveChangeClass?.show && (
          <CastScheduleOnItemMoveChangeClass
            setStateOrders={setStateOrders}
            orders={[...orders]}
          />
        )}
        {castScheduleOnItemResize?.show && (
          <CastScheduleOnItemResize
            setStateOrders={setStateOrders}
            orders={orders}
          />
        )}
        {castScheduleOnItemCreate?.show && (
          <CastScheduleOnItemCreate
            setStateOrders={setStateOrders}
            orders={orders}
          />
        )}
        {castScheduleOnItemCreateNoCast?.show && (
          <CastScheduleOnItemCreateNoCast
            setStateOrders={setStateOrders}
            orders={orders}
          />
        )}
        {castScheduleOnShiftMove?.show && (
          <CastScheduleOnShiftMove
            setStateShifts={setStateShifts}
            shifts={castShifts}
          />
        )}
        {castSchedule?.show && (
          <FormModal<CreateCastShiftReq, UpdateCastShiftReq>
            formId="castSchedule"
            title="キャストシフト"
            forms={forms}
            addType={CreateCastShiftReq}
            addFunc={(formData) => addCastShift(companyId, formData)}
            updateType={UpdateCastShiftReq}
            updateFunc={(formData) => updateCastShift(companyId, formData)}
          />
        )}
        <Dialog
          open={!!pendingRoomMove}
          onClose={() => setPendingRoomMove(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>部屋アサインの変更</DialogTitle>
          <DialogContent>
            {pendingRoomMove && (
              <DialogContentText component="div">
                <Box mb={1}>
                  <strong>{pendingRoomMove.castDisplayName}</strong> を以下のとおり変更します。
                </Box>
                <Box mb={0.5} style={{ color: "#888" }}>
                  変更前: {pendingRoomMove.fromLabel}
                </Box>
                <Box style={{ color: "#1976d2", fontWeight: 600 }}>
                  変更後: {pendingRoomMove.toLabel}
                </Box>
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingRoomMove(null)} color="default">
              キャンセル
            </Button>
            <Button
              onClick={() => {
                if (!pendingRoomMove) return;
                const p = pendingRoomMove;
                // ベースは stateShifts (既に確定済の変更を含む) を採用する。
                // ここで castShifts (redux) を base にすると、直前に確定した別キャストの
                // 変更が redux の古い値で巻き戻ってしまうため不可。
                const nextShifts = stateShifts.map((s: any) =>
                  s.castShiftId === p.shiftId
                    ? {
                        ...s,
                        planWorkStart: p.newPlanWorkStart,
                        planWorkEnd: p.newPlanWorkEnd,
                        roomId: p.newRoomId,
                      }
                    : s
                );
                setStateShifts(nextShifts);
                CastShiftApi.updateRoomAssignment(companyId, {
                  castShiftId: p.shiftId,
                  roomId: p.newRoomId,
                  planWorkStart: p.newPlanWorkStart,
                  planWorkEnd: p.newPlanWorkEnd,
                }).catch((e) => {
                  console.error("updateRoomAssignment failed", e);
                  alert("部屋アサインの更新に失敗しました。再読込してください。");
                });
                setPendingRoomMove(null);
              }}
              color="primary"
              variant="contained"
            >
              確定する
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};
export default CastSchedule;
